import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Minus,
  Plus,
  ArrowLeft,
  Truck,
  ShieldCheck,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  BadgeCheck,
  Trash2,
  Headphones as HeadsetIcon,
} from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Stars from "../components/Stars";
import WishlistButton from "../components/WishlistButton";
import PhotoUploader from "../components/PhotoUploader";
import Lightbox from "../components/Lightbox";
import { formatPrice } from "../lib/format";
import FrequentlyBoughtBundle from "../components/FrequentlyBoughtBundle";
import VariantPicker from "../components/VariantPicker";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [reviews, setReviews] = useState({ items: [], avg_rating: 0, count: 0 });
  const [form, setForm] = useState({ rating: 5, title: "", body: "", photos: [] });
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState({ photos: [], index: 0, open: false });

  const openLightbox = (photos, index) =>
    setLightbox({ photos, index, open: true });
  const closeLightbox = () => setLightbox((s) => ({ ...s, open: false }));

  useEffect(() => {
    setLoading(true);
    setActiveIdx(0);
    setSelectedVariant(null);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // Auto-select the first in-stock variant for variant-based products
        const vs = r.data?.variants || [];
        if (vs.length > 0) {
          const firstAvailable = vs.find((v) => v.stock > 0) || vs[0];
          setSelectedVariant(firstAvailable);
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    api
      .get(`/products/${id}/reviews`)
      .then((r) => setReviews(r.data))
      .catch(() => setReviews({ items: [], avg_rating: 0, count: 0 }));
  }, [id]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const extras = Array.isArray(product.images) ? product.images : [];
    const all = [product.image, ...extras].filter(Boolean);
    return Array.from(new Set(all));
  }, [product]);

  // JSON-LD Schema.org Product (rich snippets Google)
  useEffect(() => {
    if (!product) return;
    const ld = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      description: product.description,
      image: gallery,
      sku: product.id,
      brand: { "@type": "Brand", name: "Nord Stella Tech" },
      offers: {
        "@type": "Offer",
        url: typeof window !== "undefined" ? window.location.href : "",
        priceCurrency: "EUR",
        price: product.price.toFixed(2),
        availability:
          product.stock > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
      },
    };
    if (reviews.count > 0) {
      ld.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: reviews.avg_rating,
        reviewCount: reviews.count,
        bestRating: 5,
        worstRating: 1,
      };
      ld.review = reviews.items.slice(0, 5).map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.user_name },
        datePublished: r.created_at.slice(0, 10),
        reviewBody: r.body,
        name: r.title,
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
      }));
    }
    let tag = document.getElementById("product-jsonld");
    if (!tag) {
      tag = document.createElement("script");
      tag.id = "product-jsonld";
      tag.type = "application/ld+json";
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(ld);
    return () => {
      const t = document.getElementById("product-jsonld");
      if (t) t.remove();
    };
  }, [product, reviews, gallery]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Connectez-vous pour laisser un avis");
      navigate("/login");
      return;
    }
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Titre et avis requis");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/products/${id}/reviews`, form);
      toast.success("Merci pour votre avis !");
      setForm({ rating: 5, title: "", body: "", photos: [] });
      const r = await api.get(`/products/${id}/reviews`);
      setReviews(r.data);
      const p = await api.get(`/products/${id}`);
      setProduct(p.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteReview = async (rid) => {
    if (!confirm("Supprimer cet avis ?")) return;
    try {
      await api.delete(`/products/${id}/reviews/${rid}`);
      const r = await api.get(`/products/${id}/reviews`);
      setReviews(r.data);
      const p = await api.get(`/products/${id}`);
      setProduct(p.data);
      toast.success("Avis supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading)
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center text-sm text-white/60">
        Chargement...
      </div>
    );
  if (!product)
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center">
        <p className="text-sm text-white/60">Produit introuvable.</p>
        <Link
          to="/shop"
          className="mt-4 inline-block text-xs font-bold uppercase tracking-widest text-white underline"
        >
          Retour à la boutique
        </Link>
      </div>
    );

  const hasMany = gallery.length > 1;
  const prev = () =>
    setActiveIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const next = () => setActiveIdx((i) => (i + 1) % gallery.length);
  const userHasReview = user && reviews.items.some((r) => r.user_id === user.id);

  return (
    <div
      className="max-w-7xl mx-auto px-6 md:px-12 py-12"
      data-testid="product-detail-page"
    >
      <button
        onClick={() => navigate(-1)}
        className="text-xs font-bold uppercase tracking-[0.25em] inline-flex items-center gap-2 text-white/70 hover:text-white"
        data-testid="back-button"
      >
        <ArrowLeft size={14} /> Retour
      </button>

      <div className="mt-8 grid md:grid-cols-2 gap-12 items-start">
        <div className="space-y-4" data-testid="product-gallery">
          <div className="relative bg-[#0A2A4F] aspect-square overflow-hidden border border-white/5 group">
            <img
              key={gallery[activeIdx]}
              src={gallery[activeIdx]}
              alt={`${product.name} - vue ${activeIdx + 1}`}
              className="w-full h-full object-cover transition-opacity duration-300"
              data-testid="product-main-image"
            />
            {hasMany && (
              <>
                <button
                  onClick={prev}
                  aria-label="Image précédente"
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#001F3F]/80 backdrop-blur-sm border border-white/20 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hover:text-[#001F3F]"
                  data-testid="gallery-prev"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={next}
                  aria-label="Image suivante"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#001F3F]/80 backdrop-blur-sm border border-white/20 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hover:text-[#001F3F]"
                  data-testid="gallery-next"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="absolute bottom-3 right-3 bg-[#001F3F]/80 backdrop-blur-sm border border-white/20 text-white/90 text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                  {activeIdx + 1} / {gallery.length}
                </div>
              </>
            )}
          </div>
          {hasMany && (
            <div className="grid grid-cols-5 gap-2" data-testid="gallery-thumbnails">
              {gallery.map((src, i) => (
                <button
                  key={src + i}
                  onClick={() => setActiveIdx(i)}
                  className={`aspect-square overflow-hidden border transition ${
                    i === activeIdx
                      ? "border-white"
                      : "border-white/10 hover:border-white/40 opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={src} alt={`miniature ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
                {product.category === "smart" ? "Smart Tech" : "Wellness"}
              </div>
              {product.best_seller && (
                <div
                  className="mt-3 inline-flex items-center gap-1.5 bg-[#FFD700] text-[#001F3F] text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1.5"
                  data-testid="best-seller-badge"
                >
                  ★ Best-Seller
                </div>
              )}
              <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold uppercase tracking-tighter leading-[0.95] text-white">
                {product.name}
              </h1>
            </div>
            <WishlistButton
              productId={product.id}
              productName={product.name}
              size={22}
            />
          </div>

          {reviews.count > 0 && (
            <a
              href="#reviews"
              className="mt-4 inline-flex items-center gap-2 hover:opacity-80"
              data-testid="rating-anchor"
            >
              <Stars value={reviews.avg_rating} size={16} />
              <span className="text-xs text-white/70 uppercase tracking-widest">
                {reviews.avg_rating.toFixed(1)} · {reviews.count} avis
              </span>
            </a>
          )}

          <div className="mt-6 text-3xl font-bold text-white" data-testid="product-price">
            {formatPrice(
              product.price + (selectedVariant?.price_delta || 0)
            )}
          </div>
          <p className="mt-6 text-base leading-relaxed text-white/70 max-w-md">
            {product.description}
          </p>

          {/* Variant picker — only renders if product has variants */}
          <VariantPicker
            product={product}
            selectedVariantId={selectedVariant?.id || null}
            onSelect={setSelectedVariant}
          />

          <div className="mt-10 border-t border-white/10 pt-8">
            <div className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-[#C5CDD8]">
              Quantité
            </div>
            <div className="flex items-center gap-6">
              <div className="inline-flex items-center border border-white/40">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="p-3 text-white hover:bg-white hover:text-[#001F3F] transition"
                  data-testid="qty-minus"
                >
                  <Minus size={14} />
                </button>
                <span className="w-12 text-center font-bold text-white" data-testid="qty-value">
                  {qty}
                </span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="p-3 text-white hover:bg-white hover:text-[#001F3F] transition"
                  data-testid="qty-plus"
                >
                  <Plus size={14} />
                </button>
              </div>
              <span
                className={`text-xs uppercase tracking-widest font-bold ${
                  product.stock === 0
                    ? "text-[#FF6B6B]"
                    : product.stock <= 8
                    ? "text-[#FF6B6B]"
                    : "text-white/60"
                }`}
                data-testid="stock-indicator"
              >
                {product.stock === 0
                  ? "Épuisé"
                  : product.stock <= 8
                  ? `⚠ Plus que ${product.stock} en stock !`
                  : `Stock : ${product.stock}`}
              </span>
            </div>

            {/* Reassurance banner — high-conversion guarantee badge */}
            <div
              className="mt-5 flex items-center gap-3 border border-[#A6E0B5]/40 bg-gradient-to-r from-[#A6E0B5]/10 to-transparent px-4 py-3"
              data-testid="guarantee-banner"
            >
              <ShieldCheck size={28} strokeWidth={1.5} className="text-[#A6E0B5] flex-shrink-0" />
              <div className="leading-tight">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#A6E0B5]">
                  {t("guarantee.title")}
                </div>
                <div className="text-xs text-white/85 mt-0.5">
                  {t("guarantee.subtitle")}
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                const hasVariants = (product.variants || []).length > 0;
                if (hasVariants) {
                  if (!selectedVariant) {
                    toast.error("Veuillez sélectionner une variante");
                    return;
                  }
                  if (selectedVariant.stock === 0) return;
                  add(product, qty, selectedVariant);
                } else {
                  if (product.stock === 0) return;
                  add(product, qty);
                }
              }}
              disabled={
                (product.variants || []).length > 0
                  ? !selectedVariant || selectedVariant.stock === 0
                  : product.stock === 0
              }
              className="cta-primary mt-8 w-full uppercase text-sm py-5 px-4 disabled:cursor-not-allowed"
              data-testid="add-to-cart-detail"
            >
              {(() => {
                const hasVariants = (product.variants || []).length > 0;
                const variantOut = hasVariants && (!selectedVariant || selectedVariant.stock === 0);
                const productOut = !hasVariants && product.stock === 0;
                if (productOut || variantOut) return t("common.out_of_stock");
                const unitPrice = product.price + (selectedVariant?.price_delta || 0);
                return `🛒 ${t("common.buy_now")} — ${formatPrice(unitPrice * qty)}`;
              })()}
            </button>

            {/* Secondary CTA - subtle, ghost style */}
            <button
              onClick={() => navigate("/cart")}
              className="mt-3 w-full border border-white/40 bg-transparent text-white hover:bg-white hover:text-[#001F3F] transition-all uppercase tracking-[0.25em] text-[10px] font-bold py-3"
              data-testid="view-cart-detail"
            >
              {t("common.view_cart")}
            </button>
          </div>

          {/* Trust badges row */}
          <div
            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs"
            data-testid="trust-badges"
          >
            {[
              { i: ShieldCheck, t: t("trust.secure_payment"), tid: "trust-secure-payment" },
              { i: RotateCcw, t: t("trust.satisfied_or_refunded"), tid: "trust-satisfied-refunded" },
              { i: HeadsetIcon, t: t("trust.support_7d7"), tid: "trust-support-7d7" },
            ].map((v, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 border border-white/10 bg-white/[0.03] px-3 py-3"
                data-testid={v.tid}
              >
                <v.i size={20} strokeWidth={1.5} className="text-[#A6E0B5] flex-shrink-0" />
                <span className="text-[11px] leading-tight text-white/80 font-medium">
                  {v.t}
                </span>
              </div>
            ))}
          </div>

          {/* Shipping info */}
          <div className="mt-6 grid grid-cols-2 gap-4 text-xs text-white/70">
            <div className="flex items-center gap-2">
              <Truck size={16} strokeWidth={1.5} className="text-[#C5CDD8]" />
              <span>{t("product.delivery_time")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck size={16} strokeWidth={1.5} className="text-[#C5CDD8]" />
              <span>{t("product.free_shipping")}</span>
            </div>
          </div>
        </div>
      </div>

      {/* FREQUENTLY BOUGHT TOGETHER */}
      <FrequentlyBoughtBundle product={product} />

      {/* REVIEWS SECTION */}
      <section
        id="reviews"
        className="mt-24 border-t border-white/10 pt-16 scroll-mt-24"
        data-testid="reviews-section"
      >
        <div className="grid md:grid-cols-3 gap-12 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
              Avis clients
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold uppercase tracking-tighter leading-[0.95] text-white">
              Ce qu'en disent<br />nos clients
            </h2>
            {reviews.count > 0 ? (
              <div className="mt-6">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-5xl font-bold text-white leading-none">
                    {reviews.avg_rating.toFixed(1)}
                  </span>
                  <span className="text-sm text-white/60">/ 5</span>
                </div>
                <Stars value={reviews.avg_rating} size={20} className="mt-3" />
                <div className="mt-2 text-xs text-white/60 uppercase tracking-widest">
                  Basé sur {reviews.count} avis
                </div>
              </div>
            ) : (
              <p className="mt-6 text-sm text-white/60">
                Soyez le premier à laisser un avis !
              </p>
            )}

            {user && !userHasReview && (
              <form onSubmit={submitReview} className="mt-10 space-y-4" data-testid="review-form">
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">
                  Votre note
                </div>
                <Stars
                  value={form.rating}
                  size={28}
                  onChange={(n) => setForm({ ...form, rating: n })}
                />
                <input
                  placeholder="Titre de votre avis"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none focus:border-white"
                  data-testid="review-title-input"
                  maxLength={120}
                />
                <textarea
                  placeholder="Partagez votre expérience..."
                  rows={4}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  className="w-full border border-white/20 bg-[#0A2A4F] text-white px-3 py-2 text-sm focus:outline-none focus:border-white"
                  data-testid="review-body-input"
                  maxLength={2000}
                />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 font-bold">
                    Photos (optionnel, max 3)
                  </div>
                  <PhotoUploader
                    urls={form.photos}
                    onChange={(urls) => setForm({ ...form, photos: urls })}
                    max={3}
                    testId="review-photos"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3 disabled:opacity-50"
                  data-testid="review-submit"
                >
                  {submitting ? "Envoi..." : "Publier mon avis"}
                </button>
              </form>
            )}
            {user && userHasReview && (
              <p className="mt-8 text-xs text-white/50 italic">
                Vous avez déjà laissé un avis pour ce produit. Merci !
              </p>
            )}
            {!user && (
              <Link
                to="/login"
                className="mt-8 inline-block text-xs font-bold uppercase tracking-widest text-white underline"
              >
                Se connecter pour laisser un avis →
              </Link>
            )}
          </div>

          <div className="md:col-span-2 space-y-6" data-testid="reviews-list">
            {(() => {
              const allPhotos = reviews.items.flatMap((r) =>
                Array.isArray(r.photos) ? r.photos.map((u) => ({ url: u, review: r })) : []
              );
              if (allPhotos.length === 0) return null;
              return (
                <div
                  className="border border-white/10 bg-[#0A2A4F] p-6"
                  data-testid="customer-photos-gallery"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
                      Photos clients ({allPhotos.length})
                    </div>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {allPhotos.slice(0, 16).map((p, i) => (
                      <button
                        key={`gallery-${i}`}
                        type="button"
                        onClick={() => openLightbox(allPhotos.map((x) => x.url), i)}
                        className="block focus:outline-none focus:ring-2 focus:ring-white"
                        aria-label={`Photo client ${i + 1}`}
                        data-testid={`customer-photo-${i}`}
                      >
                        <img
                          src={p.url}
                          alt=""
                          className="aspect-square w-full object-cover border border-white/10 hover:border-white transition"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            {reviews.items.length === 0 && (
              <div className="border border-white/10 bg-[#0A2A4F] p-8 text-center text-sm text-white/60">
                Aucun avis pour le moment.
              </div>
            )}
            {reviews.items.map((r) => (
              <article
                key={r.id}
                className="border border-white/10 bg-[#0A2A4F] p-6"
                data-testid={`review-${r.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white">{r.user_name}</span>
                      {r.verified && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#C5CDD8]"
                          title="Achat vérifié"
                        >
                          <BadgeCheck size={12} /> Vérifié
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <Stars value={r.rating} size={14} />
                      <span className="text-[10px] text-white/50 uppercase tracking-widest">
                        {new Date(r.created_at).toLocaleDateString("fr-FR", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>
                  {user && (user.id === r.user_id || user.role === "admin") && (
                    <button
                      onClick={() => deleteReview(r.id)}
                      className="p-1.5 text-white/40 hover:text-white"
                      aria-label="Supprimer"
                      data-testid={`delete-review-${r.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <h3 className="mt-4 text-base font-bold text-white">{r.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70 whitespace-pre-line">
                  {r.body}
                </p>
                {Array.isArray(r.photos) && r.photos.length > 0 && (
                  <div
                    className="mt-4 flex flex-wrap gap-2"
                    data-testid={`review-photos-${r.id}`}
                  >
                    {r.photos.map((u, i) => (
                      <button
                        key={`${r.id}-photo-${i}`}
                        type="button"
                        onClick={() => openLightbox(r.photos, i)}
                        className="block focus:outline-none focus:ring-2 focus:ring-white"
                        aria-label={`Agrandir la photo ${i + 1}`}
                        data-testid={`review-photo-${r.id}-${i}`}
                      >
                        <img
                          src={u}
                          alt=""
                          className="w-20 h-20 object-cover border border-white/20 hover:border-white transition"
                        />
                      </button>
                    ))}
                  </div>
                )}
                {r.admin_reply && (
                  <div
                    className="mt-5 pl-4 border-l-2 border-[#C5CDD8]/40 bg-white/[0.03] py-3 pr-3"
                    data-testid={`admin-reply-${r.id}`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
                      Réponse de Nord Stella Tech
                      {r.admin_reply_at && (
                        <span className="ml-2 font-normal text-white/40 tracking-widest">
                          ·{" "}
                          {new Date(r.admin_reply_at).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-white/80 whitespace-pre-line">
                      {r.admin_reply}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>
      {lightbox.open && (
        <Lightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={closeLightbox}
          onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
        />
      )}
    </div>
  );
}
